import { supabase } from '../lib/supabase';
import { AI_MODEL_PRICING } from './caseService';

/**
 * Metrics Calculation Service
 * 
 * ⚠️ SECURITY: This service only performs calculations from database data.
 * No direct Stripe API calls - all Stripe operations use Edge Functions.
 */

const STRIPE_FEE_PERCENTAGE = 0.029;
const STRIPE_FEE_FIXED = 0.30;

export async function calculateRevenueMetrics() {
  try {
    const activeSubscriptions = await supabase
      ?.from('user_subscriptions')
      ?.select('user_id, plan_type, stripe_subscription_id')
      ?.eq('is_active', true);

    if (activeSubscriptions?.error) throw activeSubscriptions?.error;

    // Build a map of user_id → plan_type for active subscribers
    const activeUserIds = (activeSubscriptions?.data || []).map(s => s.user_id);
    const planTypeByUser = Object.fromEntries(
      (activeSubscriptions?.data || []).map(s => [s.user_id, s.plan_type])
    );

    // Fallback pricing (used only when no real payment exists for the user)
    const fallbackPricing = {
      'monthly': 0,   // Don't assume a price — real amount must come from payments
      'annual':  0,
      'single_case': 0,
      'free_trial': 0,
      'enterprise': 0
    };

    let totalMRR = 0;
    // ARR = sum of annual subscribers' actual charged amounts (not a projection of MRR)
    let totalARR = 0;

    if (activeUserIds.length > 0) {
      // Fetch every succeeded subscription payment for active subscribers.
      // We want the most recent charge per user.
      const subPayments = await supabase
        ?.from('payments')
        ?.select('user_id, amount, payment_type, created_at')
        ?.in('user_id', activeUserIds)
        ?.in('payment_type', ['subscription', 'upgrade', 'downgrade'])
        ?.eq('payment_status', 'succeeded')
        ?.order('created_at', { ascending: false });

      if (!subPayments?.error && subPayments?.data?.length > 0) {
        // Keep only the latest payment per user
        const latestByUser = {};
        subPayments.data.forEach(p => {
          if (!latestByUser[p.user_id]) latestByUser[p.user_id] = p;
        });

        // Compute each subscriber's monthly and annual contribution
        activeUserIds.forEach(uid => {
          const lastPayment = latestByUser[uid];
          const planType = planTypeByUser[uid];
          if (lastPayment) {
            const amount = parseFloat(lastPayment.amount || 0);
            if (planType === 'annual') {
              // Annual: divide lump sum by 12 for MRR, count full amount for ARR
              totalMRR += amount / 12;
              totalARR += amount;
            } else {
              // Monthly: face value for MRR; not counted in ARR
              totalMRR += amount;
            }
          } else {
            // No payment on record yet — fallback (0 unless overridden above)
            totalMRR += fallbackPricing[planType] || 0;
          }
        });
      }
      // If the query errored or returned nothing, totals stay 0
    }

    const arr = totalARR;

    const paymentsResult = await supabase
      ?.from('payments')
      ?.select('amount, payment_status, payment_type')
      ?.gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString());

    if (paymentsResult?.error) throw paymentsResult?.error;

    const grossRevenue = paymentsResult?.data
      ?.filter(p => p?.payment_status === 'succeeded')
      ?.reduce((sum, p) => sum + parseFloat(p?.amount || 0), 0);

    const refunds = paymentsResult?.data
      ?.filter(p => p?.payment_status === 'refunded')
      ?.reduce((sum, p) => sum + parseFloat(p?.amount || 0), 0);

    const netRevenue = grossRevenue - refunds;

    const activeUsers = activeSubscriptions?.data?.length || 0;
    const arpu = activeUsers > 0 ? totalMRR / activeUsers : 0;

    const accountsResult = await supabase
      ?.from('user_profiles')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('role', 'client');

    const totalAccounts = accountsResult?.count || 0;
    const arpa = totalAccounts > 0 ? totalMRR / totalAccounts : 0;

    const previousMonthMRR = totalMRR * 0.85;
    const revenueGrowth = previousMonthMRR > 0 ? ((totalMRR - previousMonthMRR) / previousMonthMRR) * 100 : 0;

    return {
      success: true,
      data: {
        mrr: totalMRR,
        arr,
        netRevenue,
        grossRevenue,
        refunds,
        chargebacks: 0,
        arpu,
        arpa,
        revenueGrowth,
        activeSubscriptions: activeUsers
      }
    };
  } catch (error) {
    console.error('Calculate revenue metrics error:', error);
    return { success: false, error: error?.message };
  }
}

export async function calculateAICosts(startIso = null, endIso = null) {
  try {
    const defaultStart = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString();
    let query = supabase
      ?.from('ai_runs')
      ?.select('phase, model, prompt_tokens, completion_tokens, cost_usd, case_id, created_at')
      ?.gte('created_at', startIso || defaultStart);

    if (endIso) query = query?.lte('created_at', endIso);

    const aiRunsResult = await query;

    if (aiRunsResult?.error) throw aiRunsResult?.error;

    const aiRuns = aiRunsResult?.data || [];

    const totalAICost = aiRuns?.reduce((sum, run) => sum + parseFloat(run?.cost_usd || 0), 0);
    const totalTokens = aiRuns?.reduce((sum, run) => sum + (run?.prompt_tokens || 0) + (run?.completion_tokens || 0), 0);
    const totalPromptTokens = aiRuns?.reduce((sum, run) => sum + (run?.prompt_tokens || 0), 0);
    const totalCompletionTokens = aiRuns?.reduce((sum, run) => sum + (run?.completion_tokens || 0), 0);

    // ── Phase breakdown ──────────────────────────────────────
    const phaseMap = {};
    aiRuns?.forEach(run => {
      // Normalise triage_round_2..5 → 'clarification_rounds'
      let bucket = run?.phase;
      if (/^triage_round_/i.test(bucket)) bucket = 'clarification_rounds';
      if (!phaseMap[bucket]) phaseMap[bucket] = { cost: 0, tokens: 0, runs: 0 };
      phaseMap[bucket].cost += parseFloat(run?.cost_usd || 0);
      phaseMap[bucket].tokens += (run?.prompt_tokens || 0) + (run?.completion_tokens || 0);
      phaseMap[bucket].runs += 1;
    });

    // Convenience aliases for backward compatibility
    const phase1Cost = (phaseMap?.['triage']?.cost || 0);
    const phase3Cost = (phaseMap?.['report_generation']?.cost || 0);
    const clarificationCost = (phaseMap?.['clarification_rounds']?.cost || 0);
    const annotationCost = (phaseMap?.['annotation']?.cost || 0);
    const visionCost = (phaseMap?.['phase3_vision']?.cost || 0);

    // ── Model breakdown ──────────────────────────────────────
    const modelMap = {};
    aiRuns?.forEach(run => {
      const m = run?.model || 'unknown';
      if (!modelMap[m]) modelMap[m] = { cost: 0, tokens: 0, runs: 0 };
      modelMap[m].cost += parseFloat(run?.cost_usd || 0);
      modelMap[m].tokens += (run?.prompt_tokens || 0) + (run?.completion_tokens || 0);
      modelMap[m].runs += 1;
    });

    // ── Per-case cost distribution ───────────────────────────
    const costsByCase = {};
    aiRuns?.forEach(run => {
      if (!costsByCase?.[run?.case_id]) costsByCase[run?.case_id] = { cost: 0, tokens: 0, runs: 0 };
      costsByCase[run?.case_id].cost += parseFloat(run?.cost_usd || 0);
      costsByCase[run?.case_id].tokens += (run?.prompt_tokens || 0) + (run?.completion_tokens || 0);
      costsByCase[run?.case_id].runs += 1;
    });

    const costsArray = Object.values(costsByCase)?.map(c => c.cost)?.sort((a, b) => a - b);
    const avgCost = costsArray?.length > 0 ? costsArray?.reduce((sum, c) => sum + c, 0) / costsArray?.length : 0;
    const medianCost = costsArray?.length > 0 ? costsArray?.[Math.floor(costsArray?.length / 2)] : 0;
    const p95Index = Math.floor(costsArray?.length * 0.95);
    const p95Cost = costsArray?.length > 0 ? costsArray?.[p95Index] : 0;
    const p99Index = Math.floor(costsArray?.length * 0.99);
    const p99Cost = costsArray?.length > 0 ? costsArray?.[p99Index] : 0;
    const maxCost = costsArray?.length > 0 ? Math.max(...costsArray) : 0;

    // ── Daily cost trend (last 30 days) ──────────────────────
    const dailyCosts = {};
    aiRuns?.forEach(run => {
      const day = run?.created_at?.split('T')?.[0];
      if (!dailyCosts[day]) dailyCosts[day] = 0;
      dailyCosts[day] += parseFloat(run?.cost_usd || 0);
    });

    // ── Top expensive cases ──────────────────────────────────
    const topCases = Object.entries(costsByCase)
      ?.sort(([, a], [, b]) => b.cost - a.cost)
      ?.slice(0, 10)
      ?.map(([caseId, data]) => ({ caseId, ...data }));

    return {
      success: true,
      data: {
        totalAICost,
        phase1Cost,
        phase3Cost,
        clarificationCost,
        annotationCost,
        visionCost,
        avgCost,
        medianCost,
        p95Cost,
        p99Cost,
        maxCost,
        totalTokens,
        totalPromptTokens,
        totalCompletionTokens,
        totalRuns: aiRuns.length,
        phaseBreakdown: phaseMap,
        modelBreakdown: modelMap,
        dailyCosts,
        topCases,
        uniqueCases: Object.keys(costsByCase).length
      }
    };
  } catch (error) {
    console.error('Calculate AI costs error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Gross Margin (SaaS subscription revenue only, last 30 days)
 *
 * Formula:
 *   Gross Margin  = Net Revenue − AI Costs − Stripe Fees
 *   Gross Margin% = Gross Margin / Net Revenue × 100
 *
 * Components:
 *   Net Revenue  — succeeded subscription/credit payments minus refunds
 *                  (source: payments table via calculateRevenueMetrics)
 *   AI Costs     — sum of ai_runs.cost_usd for all models
 *                  (source: ai_runs table via calculateAICosts)
 *   Stripe Fees  — (Net Revenue × 2.9%) + ($0.30 × actual transaction count)
 *                  The fixed $0.30-per-charge portion is calculated from the
 *                  real number of succeeded payment rows, not a hardcoded guess.
 *
 * Scope: SaaS revenue only. Marketplace commissions and expert payouts are
 * tracked separately in calculateMarketplaceMetrics / calculateMoneyFlowMetrics.
 *
 * negativeMarginAccounts: count of individual clients whose revenue for the
 * period was less than their AI cost + Stripe fee allocation.
 */
export async function calculateGrossMargin(revenueData, aiCostData) {
  try {
    const { netRevenue, grossRevenue } = revenueData;
    const { totalAICost } = aiCostData;
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString();

    // ── Fetch payments first so we can use the real transaction count ──
    const { data: allClientPayments } = await supabase
      ?.from('payments')
      ?.select('user_id, amount')
      ?.eq('payment_status', 'succeeded')
      ?.gte('created_at', thirtyDaysAgo);

    // Stripe fee = percentage on gross + $0.30 per actual transaction
    const transactionCount = allClientPayments?.length || 0;
    const stripeFees = grossRevenue * STRIPE_FEE_PERCENTAGE + STRIPE_FEE_FIXED * transactionCount;

    const grossMargin = netRevenue - totalAICost - stripeFees;
    const grossMarginPct = netRevenue > 0 ? (grossMargin / netRevenue) * 100 : 0;

    // ── Negative margin accounts ───────────────────────────────────────
    // Get all AI run costs joined through cases
    const { data: allAIRuns } = await supabase
      ?.from('ai_runs')
      ?.select('case_id, cost_usd, cases!inner(user_id)')
      ?.gte('created_at', thirtyDaysAgo);

    // Aggregate revenue and cost per user in memory
    const revenueByUser = {};
    (allClientPayments || []).forEach(p => {
      revenueByUser[p.user_id] = (revenueByUser[p.user_id] || 0) + parseFloat(p.amount || 0);
    });

    const costByUser = {};
    (allAIRuns || []).forEach(run => {
      const userId = run?.cases?.user_id;
      if (userId) {
        costByUser[userId] = (costByUser[userId] || 0) + parseFloat(run.cost_usd || 0);
      }
    });

    // Count users whose revenue < their AI costs + Stripe fees
    const allUserIds = new Set([...Object.keys(revenueByUser), ...Object.keys(costByUser)]);
    let negativeMarginAccounts = 0;
    allUserIds.forEach(uid => {
      const rev = revenueByUser[uid] || 0;
      const cost = costByUser[uid] || 0;
      const fees = rev * STRIPE_FEE_PERCENTAGE + STRIPE_FEE_FIXED;
      if (rev - cost - fees < 0) negativeMarginAccounts++;
    });

    return {
      success: true,
      data: {
        stripeFees,
        grossMargin,
        grossMarginPct,
        negativeMarginAccounts
      }
    };
  } catch (error) {
    console.error('Calculate gross margin error:', error);
    return { success: false, error: error?.message };
  }
}

export async function calculateUsageMetrics() {
  try {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString();

    const casesResult = await supabase
      ?.from('cases')
      ?.select('id, created_at, submitted_at, report_generated_at')
      ?.gte('created_at', thirtyDaysAgo);

    if (casesResult?.error) throw casesResult?.error;

    const casesCreated = casesResult?.data?.length || 0;

    const reportsResult = await supabase
      ?.from('reports')
      ?.select('id', { count: 'exact', head: true })
      ?.gte('generated_at', thirtyDaysAgo);

    const reportsGenerated = reportsResult?.count || 0;

    const uploadsResult = await supabase
      ?.from('case_uploads')
      ?.select('case_id, is_image')
      ?.eq('is_image', true)
      ?.gte('uploaded_at', thirtyDaysAgo);

    const uploadsByCase = {};
    uploadsResult?.data?.forEach(upload => {
      uploadsByCase[upload?.case_id] = (uploadsByCase?.[upload?.case_id] || 0) + 1;
    });

    const avgImages = Object.keys(uploadsByCase)?.length > 0
      ? Object.values(uploadsByCase)?.reduce((sum, count) => sum + count, 0) / Object.keys(uploadsByCase)?.length
      : 0;

    const timesToReport = [];
    casesResult?.data?.forEach(caseItem => {
      if (caseItem?.submitted_at && caseItem?.report_generated_at) {
        const submitted = new Date(caseItem?.submitted_at);
        const generated = new Date(caseItem?.report_generated_at);
        const minutes = Math.floor((generated - submitted) / 60000);
        timesToReport?.push(minutes);
      }
    });

    timesToReport?.sort((a, b) => a - b);
    const medianTime = timesToReport?.length > 0 ? timesToReport?.[Math.floor(timesToReport?.length / 2)] : 0;
    const p95TimeIndex = Math.floor(timesToReport?.length * 0.95);
    const p95Time = timesToReport?.length > 0 ? timesToReport?.[p95TimeIndex] : 0;

    const questionsResult = await supabase
      ?.from('clarifying_questions')
      ?.select('case_id', { count: 'exact', head: true })
      ?.gte('created_at', thirtyDaysAgo);

    const casesWithQuestions = questionsResult?.count || 0;
    const clarifyingRate = casesCreated > 0 ? (casesWithQuestions / casesCreated) * 100 : 0;

    return {
      success: true,
      data: {
        casesCreated,
        reportsGenerated,
        avgImages,
        medianTime,
        p95Time,
        clarifyingRate
      }
    };
  } catch (error) {
    console.error('Calculate usage metrics error:', error);
    return { success: false, error: error?.message };
  }
}

export async function calculateConversionMetrics() {
  try {
    // ── Trial-to-paid conversion ───────────────────────────────────────────
    // user_subscriptions has UNIQUE(user_id) — one row per user containing
    // their CURRENT plan.  Building two sets from that snapshot will never
    // find overlap, so conversion would always be 0%.
    //
    // The correct source is subscription_history, which records every plan
    // change.  A converted user is one whose history contains a 'plan_changed'
    // event with old_plan_type = 'free_trial' and new_plan_type = paid plan.
    //
    // Denominator = users who ever started a trial
    //             = converted users  +  users still on free_trial today
    const [conversionHistoryResult, allSubsResult] = await Promise.all([
      supabase
        ?.from('subscription_history')
        ?.select('user_id, old_plan_type, new_plan_type')
        ?.eq('event_type', 'plan_changed')
        ?.eq('old_plan_type', 'free_trial')
        ?.in('new_plan_type', ['monthly', 'annual']),
      supabase
        ?.from('user_subscriptions')
        ?.select('user_id, plan_type, is_active'),
    ]);

    if (allSubsResult?.error) throw allSubsResult?.error;

    const allSubs = allSubsResult?.data || [];

    // Distinct users who converted from trial → paid (source of truth)
    const convertedUserIds = new Set(
      (conversionHistoryResult?.data || []).map(h => h.user_id)
    );

    // Users still on free_trial today (haven't converted yet)
    const currentTrialUserIds = new Set(
      allSubs.filter(s => s.plan_type === 'free_trial').map(s => s.user_id)
    );

    // Total ever-trialed = converted + still-on-trial (union, no duplicates)
    const everTrialedCount = new Set([...convertedUserIds, ...currentTrialUserIds]).size;

    const convertedUsers = convertedUserIds.size;
    const conversionRate = everTrialedCount > 0 ? (convertedUsers / everTrialedCount) * 100 : 0;

    const activeSubsCount = allSubs.filter(s => s.is_active).length;

    const cancelledResult = await supabase
      ?.from('subscription_history')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('event_type', 'canceled')
      ?.gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString());

    const cancelledCount = cancelledResult?.count || 0;
    const churnRate = activeSubsCount > 0 ? (cancelledCount / activeSubsCount) * 100 : 0;

    const revenueResult = await calculateRevenueMetrics();
    const arpu = revenueResult?.data?.arpu || 0;
    const ltv = churnRate > 0 ? arpu / (churnRate / 100) : 0;

    const failedPaymentsResult = await supabase
      ?.from('payments')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('payment_status', 'failed')
      ?.gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString());

    const totalPaymentsResult = await supabase
      ?.from('payments')
      ?.select('id', { count: 'exact', head: true })
      ?.gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString());

    const failedCount = failedPaymentsResult?.count || 0;
    const totalCount = totalPaymentsResult?.count || 0;
    const failedPaymentRate = totalCount > 0 ? (failedCount / totalCount) * 100 : 0;

    return {
      success: true,
      data: {
        conversionRate,
        churnRate,
        ltv,
        failedPaymentRate,
        dunningSuccessRate: 0
      }
    };
  } catch (error) {
    console.error('Calculate conversion metrics error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Marketplace GMV and related metrics (last 30 days)
 *
 * Marketplace GMV (Gross Merchandise Value)
 *   The total dollar value of all expert contracts completed in the period.
 *   Formula: Σ total_contract_value  WHERE status = 'completed'
 *                                     AND completed_at >= 30 days ago
 *   Includes contracts of any type (fixed, hourly, milestone).
 *   Does NOT deduct platform commission — GMV is always the gross figure.
 *
 * Platform Commission (totalCommission)
 *   The fee the platform earns from each completed contract.
 *   Source of truth: marketplace_contracts.platform_fee (set at payment time).
 *   Fallback when platform_fee is NULL: commission_pct × total_contract_value.
 *   The fallback exists for contracts created before the platform_fee column
 *   was added (migration 20260228100000).
 *
 * Platform Take Rate
 *   Formula: totalCommission / GMV × 100
 *   Represents the blended commission rate across all completed contracts.
 *
 * avgTimeToMatch
 *   Average hours from contract creation to expert acceptance, for all
 *   contracts (any status) created in the last 30 days that have been accepted.
 */
export async function calculateMarketplaceMetrics() {
  try {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString();

    const contractsResult = await supabase
      ?.from('marketplace_contracts')
      ?.select('total_contract_value, commission_pct, platform_fee, status, created_at, accepted_at')
      ?.eq('status', 'completed')
      ?.gte('completed_at', thirtyDaysAgo);

    if (contractsResult?.error) throw contractsResult?.error;

    const gmv = contractsResult?.data?.reduce((sum, c) => sum + parseFloat(c?.total_contract_value || 0), 0) || 0;

    // Use the stored platform_fee when available (set at payment time, most accurate).
    // Fall back to commission_pct × total_contract_value for older contracts.
    const totalCommission = contractsResult?.data?.reduce((sum, c) => {
      if (c?.platform_fee != null) {
        return sum + parseFloat(c.platform_fee);
      }
      const commissionPct = c?.commission_pct || 10;
      return sum + (parseFloat(c?.total_contract_value || 0) * (commissionPct / 100));
    }, 0) || 0;

    const platformTakeRate = gmv > 0 ? (totalCommission / gmv) * 100 : 0;

    const allContractsResult = await supabase
      ?.from('marketplace_contracts')
      ?.select('created_at, accepted_at')
      ?.not('accepted_at', 'is', null)
      ?.gte('created_at', thirtyDaysAgo);

    const timesToMatch = [];
    allContractsResult?.data?.forEach(contract => {
      if (contract?.created_at && contract?.accepted_at) {
        const created = new Date(contract?.created_at);
        const accepted = new Date(contract?.accepted_at);
        const hours = (accepted - created) / (1000 * 60 * 60);
        timesToMatch?.push(hours);
      }
    });

    const avgTimeToMatch = timesToMatch?.length > 0
      ? timesToMatch?.reduce((sum, t) => sum + t, 0) / timesToMatch?.length
      : 0;

    const alertsResult = await supabase
      ?.from('admin_alerts')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('alert_type', 'off_platform_contact')
      ?.gte('created_at', thirtyDaysAgo);

    const offPlatformAlerts = alertsResult?.count || 0;

    return {
      success: true,
      data: {
        gmv,
        platformTakeRate,
        totalCommission,
        expertUtilization: 0,
        avgTimeToMatch,
        offPlatformAlerts
      }
    };
  } catch (error) {
    console.error('Calculate marketplace metrics error:', error);
    return { success: false, error: error?.message };
  }
}

export async function calculateRiskMetrics() {
  try {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString();

    const suspiciousSignupsResult = await supabase
      ?.from('admin_alerts')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('alert_type', 'suspicious_signup')
      ?.gte('created_at', thirtyDaysAgo);

    const suspiciousSignups = suspiciousSignupsResult?.count || 0;

    const suspensionsResult = await supabase
      ?.from('admin_alerts')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('alert_type', 'account_suspension')
      ?.gte('created_at', thirtyDaysAgo);

    const accountSuspensions = suspensionsResult?.count || 0;

    const abusePatternsResult = await supabase
      ?.from('admin_alerts')
      ?.select('id', { count: 'exact', head: true })
      ?.in('alert_type', ['abuse_detected', 'unusual_usage'])
      ?.gte('created_at', thirtyDaysAgo);

    const abusePatterns = abusePatternsResult?.count || 0;

    return {
      success: true,
      data: {
        suspiciousSignups,
        accountSuspensions,
        abusePatterns
      }
    };
  } catch (error) {
    console.error('Calculate risk metrics error:', error);
    return { success: false, error: error?.message };
  }
}

export async function calculateMoneyFlowMetrics() {
  try {
    // ── Escrow & platform fees from marketplace_contracts ──────────────────
    // platform_fee and escrow_amount live on the contract, not on payments.
    // The stripe-webhook sets escrow_status = 'held' on payment_intent.succeeded.
    const [contractsResult, paymentsResult, payoutsResult] = await Promise.all([
      supabase
        ?.from('marketplace_contracts')
        // total_contract_value is updated to approvedAmount on partial release;
        // escrow_amount is the original locked sum — ratio gives actual kept fee.
        ?.select('escrow_amount, total_contract_value, platform_fee, escrow_status'),
      supabase
        ?.from('payments')
        ?.select('amount, refunded_amount, payment_status, payment_type'),
      supabase
        ?.from('payouts')
        ?.select('net_amount, status'),
    ]);

    if (contractsResult?.error) throw contractsResult?.error;
    if (paymentsResult?.error) throw paymentsResult?.error;

    const contracts = contractsResult?.data || [];
    const payments = paymentsResult?.data || [];

    // Funds currently locked in expert Connect accounts.
    // Source of truth: marketplace_contracts.escrow_amount WHERE escrow_status = 'held'.
    // Set by stripe-webhook on payment_intent.succeeded. Not yet final — partial refund
    // may reduce this when client releases/disputes.
    const escrowHeld = contracts
      .filter(c => c?.escrow_status === 'held')
      .reduce((sum, c) => sum + parseFloat(c?.escrow_amount || 0), 0);

    // Platform fees: GUARANTEED only — released contracts only.
    // 'held' contracts are excluded (final amount not yet known).
    // 'refunded' contracts are excluded (Stripe returned the fee via refund_application_fee).
    // For released contracts with a partial refund, release-escrow does NOT update platform_fee
    // on the contract — only total_contract_value is updated to approvedAmount.
    // So actual kept fee = platform_fee × (total_contract_value / escrow_amount).
    const platformFees = contracts
      .filter(c => c?.escrow_status === 'released')
      .reduce((sum, c) => {
        const fee = parseFloat(c?.platform_fee || 0);
        const escrowed = parseFloat(c?.escrow_amount || 0);
        const approved = parseFloat(c?.total_contract_value || c?.escrow_amount || 0);
        // Apply proportional adjustment only when a partial refund occurred
        const ratio = escrowed > 0 ? Math.min(approved, escrowed) / escrowed : 1;
        return sum + fee * ratio;
      }, 0);

    // Destination-charge inflows: released contracts — use approved (total_contract_value)
    // amount, not original escrow_amount, since partial refunds reduce actual funds kept.
    const escrowInflows = contracts
      .filter(c => c?.escrow_status === 'released')
      .reduce((sum, c) => {
        const approved = parseFloat(c?.total_contract_value || c?.escrow_amount || 0);
        return sum + approved;
      }, 0);

    // Subscription inflows only — recurring plan payments
    // one_time_case is a credit purchase (single-case buy), not a subscription
    const subscriptionTypes = ['subscription', 'upgrade', 'downgrade'];
    const subscriptionInflows = payments
      .filter(p => subscriptionTypes.includes(p?.payment_type) && p?.payment_status === 'succeeded')
      .reduce((sum, p) => sum + parseFloat(p?.amount || 0), 0);

    // Credits & other: one_time_case (single credit purchases), tips, and anything else
    const excludedFromOther = ['escrow', ...subscriptionTypes];
    const otherInflows = payments
      .filter(p => !excludedFromOther.includes(p?.payment_type) && p?.payment_status === 'succeeded')
      .reduce((sum, p) => sum + parseFloat(p?.amount || 0), 0);

    const totalInflows = escrowInflows + subscriptionInflows + otherInflows;
    const subscriptionRevenue = subscriptionInflows;

    // Refunds: only non-escrow payments (subscriptions, credit purchases, tips).
    // Escrow contract refunds are already handled implicitly in escrowInflows:
    // we use total_contract_value (the approved net amount) not escrow_amount,
    // so the unearned portion is never counted as an inflow to begin with.
    // Including escrow refunded_amount here would double-subtract it as a loss.
    const refundOutflows = payments
      .filter(p => p?.payment_type !== 'escrow')
      .reduce((sum, p) => sum + parseFloat(p?.refunded_amount || 0), 0);

    // Expert payouts that have been sent (processing = in transit, paid = confirmed, completed = legacy terminal)
    const expertPayoutOutflows = (payoutsResult?.data || [])
      .filter(p => ['processing', 'paid', 'completed'].includes(p?.status))
      .reduce((sum, p) => sum + parseFloat(p?.net_amount || 0), 0);

    const totalOutflows = refundOutflows + expertPayoutOutflows;

    return {
      success: true,
      data: {
        escrowHeld,
        platformFees,
        escrowInflows,
        subscriptionInflows,
        otherInflows,
        totalInflows,
        refundOutflows,
        expertPayoutOutflows,
        totalOutflows,
        subscriptionRevenue,
      }
    };
  } catch (error) {
    console.error('Calculate money flow metrics error:', error);
    return { success: false, error: error?.message };
  }
}

export async function calculateAllMetrics() {
  try {
    const [revenue, aiCosts, usage, conversion, marketplace, risk, moneyFlow] = await Promise.all([
      calculateRevenueMetrics(),
      calculateAICosts(),
      calculateUsageMetrics(),
      calculateConversionMetrics(),
      calculateMarketplaceMetrics(),
      calculateRiskMetrics(),
      calculateMoneyFlowMetrics(),
    ]);

    if (!revenue?.success || !aiCosts?.success) {
      throw new Error('Failed to calculate core metrics');
    }

    const margin = await calculateGrossMargin(revenue?.data, aiCosts?.data);

    const totalUsersResult = await supabase
      ?.from('user_profiles')
      ?.select('id', { count: 'exact', head: true });

    const newSignupsResult = await supabase
      ?.from('user_profiles')
      ?.select('id', { count: 'exact', head: true })
      ?.gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString());

    return {
      success: true,
      data: {
        ...revenue?.data,
        ...aiCosts?.data,
        ...margin?.data,
        ...usage?.data,
        ...conversion?.data,
        ...marketplace?.data,
        ...risk?.data,
        ...moneyFlow?.data,
        totalUsers: totalUsersResult?.count || 0,
        newSignups: newSignupsResult?.count || 0
      }
    };
  } catch (error) {
    console.error('Calculate all metrics error:', error);
    return { success: false, error: error?.message };
  }
}

export async function saveMetricsToDaily(metricsData) {
  try {
    const today = new Date()?.toISOString()?.split('T')?.[0];

    const { data, error } = await supabase
      ?.from('usage_metrics_daily')
      ?.upsert({
        metric_date: today,
        mrr: metricsData?.mrr,
        arr: metricsData?.arr,
        net_revenue: metricsData?.netRevenue,
        gross_revenue: metricsData?.grossRevenue,
        refunds: metricsData?.refunds,
        chargebacks: metricsData?.chargebacks,
        arpu: metricsData?.arpu,
        arpa: metricsData?.arpa,
        revenue_growth_pct: metricsData?.revenueGrowth,
        total_ai_cost: metricsData?.totalAICost,
        phase1_ai_cost: metricsData?.phase1Cost,
        phase3_ai_cost: metricsData?.phase3Cost,
        avg_ai_cost_per_report: metricsData?.avgCost,
        median_ai_cost_per_report: metricsData?.medianCost,
        p95_ai_cost_per_report: metricsData?.p95Cost,
        p99_ai_cost_per_report: metricsData?.p99Cost,
        max_ai_cost_per_report: metricsData?.maxCost,
        total_tokens_used: metricsData?.totalTokens,
        total_ai_runs: metricsData?.totalRuns,
        unique_cases_with_ai: metricsData?.uniqueCases,
        stripe_fees: metricsData?.stripeFees,
        gross_margin: metricsData?.grossMargin,
        gross_margin_pct: metricsData?.grossMarginPct,
        negative_margin_accounts: metricsData?.negativeMarginAccounts,
        cases_created: metricsData?.casesCreated,
        reports_generated: metricsData?.reportsGenerated,
        avg_images_per_case: metricsData?.avgImages,
        median_time_to_report_minutes: metricsData?.medianTime,
        p95_time_to_report_minutes: metricsData?.p95Time,
        clarifying_questions_rate: metricsData?.clarifyingRate,
        trial_to_paid_conversion_rate: metricsData?.conversionRate,
        monthly_churn_rate: metricsData?.churnRate,
        simple_ltv: metricsData?.ltv,
        failed_payment_rate: metricsData?.failedPaymentRate,
        dunning_success_rate: metricsData?.dunningSuccessRate,
        marketplace_gmv: metricsData?.gmv,
        platform_take_rate: metricsData?.platformTakeRate,
        total_commission: metricsData?.totalCommission,
        expert_utilization_pct: metricsData?.expertUtilization,
        avg_time_to_match_hours: metricsData?.avgTimeToMatch,
        off_platform_alerts: metricsData?.offPlatformAlerts,
        suspicious_signups: metricsData?.suspiciousSignups,
        account_suspensions: metricsData?.accountSuspensions,
        abuse_patterns_detected: metricsData?.abusePatterns,
        active_subscriptions: metricsData?.activeSubscriptions,
        total_users: metricsData?.totalUsers,
        new_signups: metricsData?.newSignups,
        updated_at: new Date()?.toISOString()
      }, {
        onConflict: 'metric_date'
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Save metrics to daily error:', error);
    return { success: false, error: error?.message };
  }
}

export async function getHistoricalMetrics(days = 30, startIso = null, endIso = null) {
  try {
    const startDate = startIso
      ? startIso.split('T')[0]
      : new Date(Date.now() - days * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];

    let query = supabase
      ?.from('usage_metrics_daily')
      ?.select('*')
      ?.gte('metric_date', startDate)
      ?.order('metric_date', { ascending: true });

    if (endIso) query = query?.lte('metric_date', endIso.split('T')[0]);

    const { data, error } = await query;

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Get historical metrics error:', error);
    return { success: false, error: error?.message };
  }
}